import { Injectable, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export interface ConfirmDialogConfig {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info' | 'success';
}

export interface ConfirmDialogState {
  show: boolean;
  config: ConfirmDialogConfig | null;
}

@Injectable({
  providedIn: 'root'
})
export class ConfirmDialogService {
  // Signal for dialog state
  private dialogState = signal<ConfirmDialogState>({
    show: false,
    config: null
  });

  // Subject for handling confirmation results
  private resultSubject = new Subject<boolean>();

  // Public readonly access to dialog state
  readonly state = this.dialogState.asReadonly();

  /**
   * Show a confirmation dialog and return a promise that resolves with user's choice
   */
  confirm(config: ConfirmDialogConfig): Promise<boolean> {
    const fullConfig: ConfirmDialogConfig = {
      title: 'Confirm Action',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      type: 'warning',
      ...config
    };

    this.dialogState.set({
      show: true,
      config: fullConfig
    });

    return new Promise<boolean>((resolve) => {
      const subscription = this.resultSubject.subscribe({
        next: (result) => {
          resolve(result);
          subscription.unsubscribe();
        }
      });
    });
  }

  /**
   * Handle confirmation result
   */
  handleConfirm(): void {
    this.hide();
    this.resultSubject.next(true);
  }

  /**
   * Handle cancellation result
   */
  handleCancel(): void {
    this.hide();
    this.resultSubject.next(false);
  }

  /**
   * Hide the dialog
   */
  hide(): void {
    this.dialogState.set({
      show: false,
      config: null
    });
  }

  // Utility methods for common confirmation types
  static deleteConfirmation(itemName: string): ConfirmDialogConfig {
    return {
      title: 'Delete Confirmation',
      message: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    };
  }

  static statusChangeConfirmation(itemName: string, action: string): ConfirmDialogConfig {
    return {
      title: 'Status Change Confirmation',
      message: `Are you sure you want to ${action} "${itemName}"?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancel',
      type: 'warning'
    };
  }

  static blockConfirmation(count: number): ConfirmDialogConfig {
    return {
      title: 'Block Lots Confirmation',
      message: `Are you sure you want to block ${count} lot(s)?`,
      confirmText: 'Block',
      cancelText: 'Cancel',
      type: 'warning'
    };
  }

  static unblockConfirmation(lotNumber: string): ConfirmDialogConfig {
    return {
      title: 'Unblock Lot Confirmation',
      message: `Are you sure you want to unblock lot ${lotNumber}?\n\nThis will make the lot available for pooling.`,
      confirmText: 'Unblock',
      cancelText: 'Cancel',
      type: 'warning'
    };
  }

  static passwordResetConfirmation(userName: string): ConfirmDialogConfig {
    return {
      title: 'Reset Password Confirmation',
      message: `Are you sure you want to reset the password for ${userName}?\n\nA temporary password will be generated.`,
      confirmText: 'Reset Password',
      cancelText: 'Cancel',
      type: 'warning'
    };
  }

  static removeLocationConfirmation(): ConfirmDialogConfig {
    return {
      title: 'Remove Location',
      message: 'Are you sure you want to remove this location?',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      type: 'warning'
    };
  }
}